import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Eye, Heart, ShoppingBag } from "lucide-react";
import { ViewOnlyBanner } from "./ViewOnlyBanner";
import { resolveImageUrl } from "@/utils/imageUrl";

export const ShowcaseTab = ({
  darkMode, showcaseProducts, showcaseInquiries, showcaseSettings,
  updateShowcaseSettings, deleteShowcase,
  setEditingShowcase, setShowcaseForm, setShowcaseDialogOpen, viewOnly
}) => {
  const cardBg = darkMode ? "bg-gray-800/80 border border-gray-700/50" : "";

  return (
    <>
    {viewOnly && <ViewOnlyBanner darkMode={darkMode} />}
    <Card className={`border-0 shadow-soft rounded-3xl ${cardBg}`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <CardTitle className={darkMode ? "text-gray-100" : ""}>Product Showcase</CardTitle>
          <div className="flex items-center gap-3">
            {!viewOnly && (
            <Button onClick={() => { setEditingShowcase(null); setShowcaseForm({ title: "", description: "", price: "", image_url: "", category: "Books", is_published: true }); setShowcaseDialogOpen(true); }} className="rounded-xl bg-primary" data-testid="add-showcase-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Product
            </Button>
            )}
          </div>
        </div>
        {!viewOnly && (
        <div className={`flex flex-wrap items-center gap-6 pt-4 border-t mt-4 ${darkMode ? "border-gray-700" : ""}`}>
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${darkMode ? "text-gray-300" : ""}`}>
            <input type="checkbox" checked={showcaseSettings.show_views} onChange={e => updateShowcaseSettings("show_views", e.target.checked)} className="rounded" />
            <Eye className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            Show view counts
          </label>
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${darkMode ? "text-gray-300" : ""}`}>
            <input type="checkbox" checked={showcaseSettings.show_likes} onChange={e => updateShowcaseSettings("show_likes", e.target.checked)} className="rounded" />
            <Heart className={`w-4 h-4 ${darkMode ? "text-gray-400" : "text-gray-500"}`} />
            Show like counts
          </label>
        </div>
        )}
      </CardHeader>
      <CardContent>
        {showcaseProducts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {showcaseProducts.map(product => (
              <div key={product.id} className={`border rounded-2xl overflow-hidden group ${darkMode ? "border-gray-700 bg-gray-800/50" : ""}`} data-testid={`showcase-admin-${product.id}`}>
                <div className={`aspect-square relative overflow-hidden ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                  {product.image_url && <img src={resolveImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" />}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {!product.is_published && <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">Draft</span>}
                  </div>
                </div>
                <div className="p-4">
                  <h4 className={`font-bold text-sm mb-1 line-clamp-1 ${darkMode ? "text-gray-200" : ""}`}>{product.title}</h4>
                  <p className={`text-xs line-clamp-2 mb-2 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{product.description}</p>
                  <div className={`flex items-center gap-3 text-xs mb-3 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {product.views || 0}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {product.likes || 0}</span>
                    <span className={`px-2 py-0.5 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>{product.category}</span>
                  </div>
                  {!viewOnly && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className={`flex-1 rounded-lg ${darkMode ? "bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600" : ""}`} onClick={() => { setEditingShowcase(product); setShowcaseForm({ title: product.title, description: product.description, price: product.price || "", image_url: product.image_url, category: product.category || "Books", is_published: product.is_published }); setShowcaseDialogOpen(true); }}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button variant="destructive" size="sm" className="rounded-lg" onClick={() => deleteShowcase(product.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <ShoppingBag className={`w-16 h-16 mx-auto mb-4 ${darkMode ? "text-gray-600" : "text-muted-foreground"}`} />
            <p className={`mb-4 ${darkMode ? "text-gray-400" : "text-muted-foreground"}`}>No showcase products yet</p>
            {!viewOnly && (
            <Button onClick={() => setShowcaseDialogOpen(true)} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Add First Product
            </Button>
            )}
          </div>
        )}

        {showcaseInquiries.length > 0 && (
          <div className={`mt-8 border-t pt-6 ${darkMode ? "border-gray-700" : ""}`}>
            <h3 className={`font-bold text-lg mb-4 ${darkMode ? "text-gray-200" : ""}`}>Recent Inquiries ({showcaseInquiries.length})</h3>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {showcaseInquiries.slice(0, 10).map(inq => (
                <div key={inq.id} className={`rounded-xl p-4 text-sm ${darkMode ? "bg-gray-700/50" : "bg-gray-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-medium ${darkMode ? "text-gray-200" : ""}`}>{inq.name} ({inq.email})</span>
                    <span className={`text-xs ${darkMode ? "text-gray-500" : "text-gray-400"}`}>{new Date(inq.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs text-accent mb-1">Re: {inq.product_title}</p>
                  <p className={darkMode ? "text-gray-400" : "text-gray-600"}>{inq.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};
